import { useState } from "react"
import { router } from "@inertiajs/react"
import { AdminLayout } from "@/components/Admin/AdminLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

interface OrderSummary {
  id: number
  total_cents: number
  status: string
  created_at: string
}

interface ProductItem {
  id: number
  name: string
  slug: string
  base_product_ids: number[]
}

interface UserDetail {
  id: number
  email: string
  name: string
  created_at: string
  orders_count: number
  has_advanced_test_access: boolean
  has_test_result: boolean
  confirmed_at?: string | null
  product_access: Record<string, boolean>
  orders: OrderSummary[]
  test_result?: {
    tipo_eneagrama: number
    completed_at?: string | null
  } | null
}

interface Props {
  user: UserDetail
  products: ProductItem[]
}

const formatCents = (cents: number) => (cents / 100).toFixed(2).replace(".", ",")

const formatProductAccess = (productAccess: Record<string, boolean>) => {
  const enabled = Object.entries(productAccess)
    .filter(([, value]) => value)
    .map(([key]) => key)
  return enabled.length ? enabled.join(", ") : "—"
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date)
}

function hasAccessToProduct(productAccess: Record<string, boolean>, baseProductIds: number[]) {
  if (baseProductIds.length === 0) return false
  return baseProductIds.every((id) => productAccess[String(id)] === true)
}

export default function UsersShow({ user, products }: Props) {
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleProduct = (id: number) => {
    const next = new Set(selectedProducts)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedProducts(next)
  }

  const handleGrantAccess = () => {
    if (selectedProducts.size === 0 || isSubmitting) return
    setIsSubmitting(true)
    router.post(`/admin/users/${user.id}/grant_access`, {
      product_ids: Array.from(selectedProducts)
    }, {
      onFinish: () => setIsSubmitting(false)
    })
  }

  return (
    <AdminLayout title={`Usuário #${user.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2">
          <h2 className="text-lg font-semibold mb-3">Dados do usuário</h2>
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Nome:</span> {user.name || "—"}</div>
            <div><span className="text-muted-foreground">E-mail:</span> {user.email}</div>
            <div><span className="text-muted-foreground">Criado:</span> {formatDateTime(user.created_at)}</div>
            <div><span className="text-muted-foreground">Confirmado:</span> {formatDateTime(user.confirmed_at)}</div>
            <div><span className="text-muted-foreground">Teste avançado:</span> {user.has_advanced_test_access ? "Sim" : "Não"}</div>
          </div>
        </Card>

        <Card className="p-4">
          <h2 className="text-lg font-semibold mb-3">Resumo</h2>
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Pedidos:</span> {user.orders_count}</div>
            <div><span className="text-muted-foreground">Resultado:</span> {user.has_test_result ? "Sim" : "Não"}</div>
            <div><span className="text-muted-foreground">Produtos:</span> {formatProductAccess(user.product_access)}</div>
          </div>
        </Card>
      </div>

      <Card className="p-4 md:p-6 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <h2 className="text-lg font-semibold mb-2 text-amber-900">Liberar acesso (cortesia)</h2>
        <p className="text-sm text-amber-700 mb-4">
          Cria um pedido cortesia com valor R$ 0,00 para liberar acesso aos produtos selecionados.
        </p>

        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum produto ativo cadastrado.</p>
        ) : (
          <div className="space-y-3">
            {products.map((product) => {
              const alreadyHasAccess = hasAccessToProduct(user.product_access, product.base_product_ids)
              return (
                <label
                  key={product.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    alreadyHasAccess
                      ? "bg-green-50 border-green-200 opacity-60"
                      : "bg-white border-gray-200 hover:border-amber-300 cursor-pointer"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={alreadyHasAccess || selectedProducts.has(product.id)}
                    disabled={alreadyHasAccess}
                    onChange={() => toggleProduct(product.id)}
                    className="h-4 w-4 accent-amber-600"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium">{product.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">({product.slug})</span>
                  </div>
                  {alreadyHasAccess && (
                    <span className="text-xs text-green-700 font-medium">Já possui</span>
                  )}
                </label>
              )
            })}

            <Button
              onClick={handleGrantAccess}
              disabled={selectedProducts.size === 0 || isSubmitting}
              className="mt-2 bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? "Concedendo..." : "Conceder acesso"}
            </Button>
          </div>
        )}
      </Card>

      <Card className="p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-4">Pedidos</h2>
        {user.orders.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum pedido encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Total</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Criado</th>
                </tr>
              </thead>
              <tbody>
                {user.orders.map((order) => (
                  <tr key={order.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">#{order.id}</td>
                    <td className="py-2 pr-4">R$ {formatCents(order.total_cents)}</td>
                    <td className="py-2 pr-4 capitalize">{order.status}</td>
                    <td className="py-2">{formatDateTime(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-4">Resultado do teste</h2>
        {user.test_result ? (
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Tipo Eneagrama:</span> {user.test_result.tipo_eneagrama}</div>
            <div><span className="text-muted-foreground">Concluído:</span> {formatDateTime(user.test_result.completed_at)}</div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Sem resultado registrado.</p>
        )}
      </Card>
    </AdminLayout>
  )
}
